import * as React from 'react';
import HistoryRowControls from 'Components/HistoryRowControls';
import type { IHistoryContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import { filterConfirmedSeizures, formatSeizureClassification } from '../../utils';
import type { TSeizureDocumentType } from '../../utils';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import DataTable from 'Components/DataTable';
import { formatTime, isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import colors from '../../../../config/theme/colors';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ToolTip from 'Components/ToolTip';
import Icon from 'Components/_NewElements/Icon';
import { filterByDocumentType, sortDocumentsDescending } from 'Utility/documentHandling';

// Same as in HistoryRowControls
const StyledMenuIcon = styled(({ onClick }: { onClick?: (e: React.MouseEvent<Element | HTMLElement>) => void }) => (
  <IconButton onClick={onClick}>
    <MoreVertIcon />
  </IconButton>
))({
  color: '#6c96ae',
  cursor: 'pointer',
});

const ConfirmedSeizures = ({
  documents,
  patientReportedDocs,
  fm,
  startEdit,
  setPatientReportedSeizureFormOpen,
}: IOwnProps): React.JSX.Element => {
  if (!startEdit) throw new Error('fm or startEdit is undefined');
  const allSeizureDocs = filterByDocumentType(documents, 'seizure');

  // Docs that are confirmed
  const confirmedDocs = filterConfirmedSeizures([...allSeizureDocs, ...patientReportedDocs]);

  const listedDocs: Array<IDocumentWithTypeV2<'seizure' | 'patientReportedSeizure'>> = [...confirmedDocs].sort(
    sortDocumentsDescending,
  );

  const [anchor, setAnchor] = React.useState<null | { anchor: React.MouseEvent['currentTarget']; index: number }>(null);

  const toggleMenu =
    (index: number) =>
    (e: React.MouseEvent): void => {
      setAnchor(!anchor ? { anchor: e.currentTarget, index } : null);
    };

  const startEditViewPatientReported = (
    document: IDocumentWithTypeV2<'patientReportedSeizure'>,
    type: 'view' | 'edit',
  ) => {
    setAnchor(null);
    setPatientReportedSeizureFormOpen({ doc: document, type });
  };

  const listedDocsHistoryData: (
    | string
    | number
    | React.JSX.Element
    | (string | number | React.JSX.Element | number[] | string[])[]
  )[] = listedDocs.map((d, i) => [
    <div key={d._id} style={{ paddingRight: '3rem', width: '100%' }}>
      {d._type === 'patientReportedSeizure' && (
        <ToolTip
          title={fm('seizure.reportedSeizure')}
          hover
          arrow={false}
          content={
            <div style={{ width: '0', height: '0', position: 'relative', left: '-3.5rem' }}>
              <Icon icon="sqicon" />
            </div>
          }
        />
      )}
      <div style={{ fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' }}>
        {formatPartialDate('date' in d ? d.date : 'startDate' in d ? d.startDate : undefined)}
        {d.endDate && (!('startTime' in d) || !d.startTime || !('time' in d) || !d.time)
          ? '\u00A0–\u00A0'
          : ('startTime' in d && d.startTime) || ('time' in d && d.time)
            ? '\u00A0'
            : ''}
      </div>
      <div
        style={{
          fontWeight: d.endDate && isPartialDate(d.endDate) ? 600 : 400,
          display: 'inline-block',
          whiteSpace: 'nowrap',
        }}
      >
        {d.endDate
          ? `${formatPartialDate(d.endDate)}`
          : 'startTime' in d
            ? ` ${formatTime(d.startTime)}`
            : 'time' in d
              ? `${formatTime(d.time)}`
              : ''}
      </div>
    </div>,
    d.count ?? '',
    formatSeizureClassification(
      'classification' in d ? d.classification : 'seizureType' in d ? (d.seizureType ?? undefined) : undefined,
      'long',
    ),
    d.seizureMedicationGiven ? (
      <div key={'seizureMedicationGiven_' + d._id} style={{ display: 'flex' }}>
        <CheckCircleIcon style={{ marginRight: '0.5rem', color: colors.success }} />
      </div>
    ) : (
      ''
    ),
    d.medicCompliance === 'yes' ? (
      <div key={'medicCompliance_yes_' + d._id} style={{ display: 'flex' }}>
        <CheckCircleIcon style={{ marginRight: '0.5rem', color: colors.success }} />
        {fm('general.yes')}
      </div>
    ) : d.medicCompliance === 'no' ? (
      <div key={'medicCompliance_no_' + d._id} style={{ display: 'flex' }}>
        <WarningIcon style={{ marginRight: '0.5rem', color: colors.warning.dark }} />
        {fm('general.no')}
      </div>
    ) : (
      '-'
    ),
    <div key={`${d._id}-desc`} style={{ display: 'flex' }}>
      {'freeDescription' in d && (
        <ToolTip
          description={d.freeDescription ?? undefined}
          content={<ManageSearchIcon style={{ color: colors.primary }} />}
          hover={true}
        />
      )}
    </div>,
    <div key={`${d._id}-controls`} style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {d._type === 'seizure' ? (
        <HistoryRowControls document={d} startEdit={startEdit} hideButton showEditInMenu />
      ) : (
        <div key={d._id} style={{ width: '4.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <StyledMenuIcon onClick={toggleMenu(i)} />
          <Menu
            anchorEl={anchor?.index === i ? anchor.anchor : undefined}
            open={anchor?.index === i ? Boolean(anchor.anchor) : false}
            onClose={() => setAnchor(null)}
          >
            <MenuItem onClick={() => startEditViewPatientReported(d, 'edit')}>{fm('general.edit')}</MenuItem>
          </Menu>
        </div>
      )}
    </div>,
  ]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flex: 1,
          justifyContent: 'space-between',
          marginBottom: '2rem',
        }}
      >
        <HistoryRowSubHeader header={fm('seizure.confirmedSeizures')} />
      </div>
      <DataTable
        headers={[
          fm('patientReportedSeizure.timeframe'),
          fm('seizure.count'),
          fm('seizure.classification'),
          fm('seizure.seizureMedicationGiven'),
          fm('patientReportedSeizure.medicCompliance'),
          fm('patientReportedSeizure.freeDescription'),
          '',
        ]}
        data={{ rowData: listedDocsHistoryData }}
      />
    </>
  );
};

interface IOwnProps extends IHistoryContext {
  documents: Array<IDocumentWithTypeV2<TSeizureDocumentType>>;
  patientReportedDocs: Array<IDocumentWithTypeV2<'patientReportedSeizure'>>;
  setPatientReportedSeizureFormOpen: React.Dispatch<
    React.SetStateAction<
      | {
          doc: IDocumentWithTypeV2<'patientReportedSeizure'>;
          type: 'edit' | 'view';
        }
      | undefined
    >
  >;
}

export default withFormContext(ConfirmedSeizures);
